// live/FoldedObservationView.tsx (TB4) - "while you were out": the pro narrating the hand you folded out
// of, through to showdown. Rendered by BOTH do-modes (Diagnose + Solo) so folded hands teach the same way
// on either side of the ramp.
//
// REUSE, not new UI (the GH8/S20 pattern): <OvalFelt> draws the aftermath verbatim, <NarrationBeats> reveals
// the pro's read beats verbatim. The one genuinely new element is the showdown strip, and it exists because
// a beat can only TELL you what someone showed - the whole lesson of a folded hand is SEEING the cards hit
// the felt. It reuses <Card> verbatim.
//
// THE HOLE-CARD FIREWALL: this component renders cards from exactly one source, `obs.shown`, which the
// engine mints only at a real showdown (`hand/showdown.ts` reveals the unfolded seats; a pot won without a
// showdown reveals nobody). There is no other card data in a FoldedObservation to render - `WatchedAction`
// has no card field. The felt cannot leak either: `SeatView` has no card field, so a villain pod has never
// been able to draw a card in this app.
import { Card } from '../components/Card';
import { OvalFelt } from '../components/OvalFelt';
import { ArchetypeTerm } from '../components/ArchetypeTerm';
import { NarrationBeats } from '../observe/NarrationBeats';
import { archetypeOf } from '../engine/liveData';
import { displayName } from '../engine/liveRunner';
import type { FoldedObservation } from './foldedObservation';

export interface FoldedObservationViewProps {
  obs: FoldedObservation;
  /** back to the grade you just got. */
  onBack: () => void;
  /** on into the locked fold path: the hand report. */
  onReport: () => void;
}

export function FoldedObservationView({ obs, onBack, onReport }: FoldedObservationViewProps) {
  return (
    <>
      <OvalFelt view={obs.felt} />

      <div className="body">
        <div className="cf-head">
          <span className="cf-tag">{obs.kicker}</span>
          <div className="h2">{obs.h2}</div>
          <div className="lede">{obs.lede}</div>
        </div>

        <NarrationBeats beats={obs.beats} />

        {obs.shown.length > 0 && (
          <div className="showdown">
            <div className="showdown-k">Turned over</div>
            {obs.shown.map((s) => (
              <div key={s.seat} className={'showdown-row' + (s.won ? ' won' : '')}>
                <div className="showdown-who">
                  {/* CX9: tap the type he turned out to be to learn what it is + how to adjust. */}
                  <ArchetypeTerm id={s.archetypeId} variant="chip">
                    {displayName(s.archetypeId)}
                  </ArchetypeTerm>
                  <span className="showdown-arch">{archetypeOf(s.archetypeId).label}</span>
                </div>
                <div className="showdown-cards">
                  <Card card={s.cards[0]} />
                  <Card card={s.cards[1]} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="dock">
        <div className="dockrow">
          <button type="button" className="cta ghost prev" onClick={onBack}>
            Back
          </button>
          <button type="button" className="cta" onClick={onReport}>
            See the hand report
          </button>
        </div>
      </div>
    </>
  );
}
